import type { CommentDeleteRequest, CommentRequest } from '@eformulieren/renderer'
import { CLIENT_HEADER, WRITE_HEADERS } from './podRequests.js'

/**
 * Een opmerking van een meelezer langs de pod (`POST /opmerkingen?instantie=`, R-73, besluit 8 van 2
 * november 2026, api-contract afwijking 7). Een nieuwe opmerking draagt het geselecteerde bereik in het
 * rich-text-veld (`bereik`, besluit 12.41); de pod zet daar het anker.
 *
 * De meelezer schrijft niet in zijn eigen sectiedocument: de pod neemt dat niet aan, en dan loopt het
 * document in deze browser uiteen met dat van de pod. De pod schrijft het bericht in het levende
 * document onder zijn eigen origin, met persoon en moment uit het token, en het komt hier binnen met de
 * update. Een weigering wordt een fout met de code, zodat de knop zijn melding toont (`BusyButton`).
 */
export class CommentRejected extends Error {
  constructor(readonly code: string | undefined, readonly status: number) {
    super(`POST /opmerkingen gaf ${String(status)}${code === undefined ? '' : `: ${code}`}`)
    this.name = 'CommentRejected'
  }
}

export async function postComment(options: {
  readonly instantieId: string
  readonly headers: Readonly<Record<string, string>>
  readonly request: CommentRequest
  readonly fetch: (input: string, init: RequestInit) => Promise<Response>
  readonly onSessionExpired: () => void
}): Promise<{ readonly draad: string }> {
  const { request } = options
  const response = await options.fetch(`/opmerkingen?instantie=${encodeURIComponent(options.instantieId)}`, {
    method: 'POST',
    headers: { ...options.headers, ...WRITE_HEADERS },
    body: JSON.stringify({
      sectie: request.sectie,
      anker: {
        ...(request.veldId === undefined ? {} : { veldId: request.veldId }),
        ...(request.bereik === undefined ? {} : { bereik: { van: request.bereik.van, tot: request.bereik.tot } }),
      },
      ...(request.draad === undefined ? {} : { draad: request.draad }),
      tekst: request.tekst,
    }),
  })
  if (response.status === 201) {
    const body = (await response.json()) as { draad?: unknown }
    if (typeof body.draad !== 'string') throw new CommentRejected(undefined, response.status)
    return { draad: body.draad }
  }
  if (response.status === 401) options.onSessionExpired()
  const text = await response.text()
  let code: string | undefined
  try {
    const parsed = JSON.parse(text) as { code?: unknown }
    code = typeof parsed.code === 'string' ? parsed.code : undefined
  } catch {
    code = undefined
  }
  console.error('eformulieren: opmerking niet geplaatst', { status: response.status, code, lijf: text.slice(0, 2000) })
  throw new CommentRejected(code, response.status)
}

/**
 * Een eigen opmerking verwijderen langs de pod (`DELETE /opmerkingen/:draad?instantie=&sectie=[&bericht=]`,
 * eisenboek 12.45), voor een meelezer, om dezelfde reden als {@link postComment}. Zonder `bericht` het hele
 * draadje. De verwijdering komt binnen met de update van de pod; een weigering wordt een fout met de code,
 * zodat de knop van de bevestiging zijn melding toont.
 */
export async function deleteComment(options: {
  readonly instantieId: string
  readonly headers: Readonly<Record<string, string>>
  readonly request: CommentDeleteRequest
  readonly fetch: (input: string, init: RequestInit) => Promise<Response>
  readonly onSessionExpired: () => void
}): Promise<{ readonly draadWeg: boolean }> {
  const { request } = options
  const query = `instantie=${encodeURIComponent(options.instantieId)}&sectie=${encodeURIComponent(request.sectie)}${request.bericht === undefined ? '' : `&bericht=${encodeURIComponent(request.bericht)}`}`
  const response = await options.fetch(`/opmerkingen/${encodeURIComponent(request.draad)}?${query}`, {
    method: 'DELETE',
    headers: { ...options.headers, ...CLIENT_HEADER },
  })
  if (response.status === 200) {
    const body = (await response.json()) as { draadWeg?: unknown }
    return { draadWeg: body.draadWeg === true }
  }
  if (response.status === 401) options.onSessionExpired()
  const text = await response.text()
  let code: string | undefined
  try {
    const parsed = JSON.parse(text) as { code?: unknown }
    code = typeof parsed.code === 'string' ? parsed.code : undefined
  } catch {
    code = undefined
  }
  console.error('eformulieren: opmerking niet verwijderd', { status: response.status, code, lijf: text.slice(0, 2000) })
  throw new CommentRejected(code, response.status)
}
